// useBots.ts — the workspace's bots. Hoisted out of `Bots.tsx` (dashboard-shell spec §7.3 /
// task-4 brief) because the rail's identity card and switcher need the list on every route,
// not just on All bots.
import { useQuery } from '@tanstack/react-query';
import type { BotDto, BotListItemDto } from '@repo/shared-types';
import { apiFetch } from '../lib/api';

/** The cheap shape. Fetched on every route by the rail — must never carry the `?health=1`
 * aggregates (spec §7.3): the one-bot majority has no popover to render them in, and the
 * rail pays for this query unconditionally, so it stays as small as `GET /api/bots` gets. */
export function useBots() {
  return useQuery({
    queryKey: ['bots'],
    queryFn: () => apiFetch<BotDto[]>('/api/bots'),
    staleTime: 60_000,
  });
}

/**
 * The health shape (Task 2's `?health=1`), for All bots and for the switcher popover.
 *
 * `BotSwitcher.tsx` used to gate this on `bots.length > 1` (the handoff's one-bot identity card
 * had no popover to render the per-bot aggregates in). Task 1 of the dashboard-shell-feedback
 * plan (2026-08-09) made the switcher always a popover, and briefly called this unconditionally
 * from that site — which turned out to fire the heaviest bot list query on every authenticated
 * route for every workspace, for a popover that starts closed. Fix-wave item 4 (2026-08-09)
 * gated it back on the popover's own `open` state. `enabled` stays a parameter (not inlined)
 * so each call site — `Bots.tsx` (always `true`, the screen itself needs it), `BotSwitcher.tsx`
 * (only `open`) — states its own reason.
 */
export function useBotsWithHealth(enabled: boolean) {
  return useQuery({
    queryKey: ['bots', 'health'],
    queryFn: () => apiFetch<BotListItemDto[]>('/api/bots?health=1'),
    enabled,
    staleTime: 60_000,
  });
}
